import React, { useState } from 'react';

import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import InstrumentTabPanel from './InstrumentTabPanel';
import ConnectInstrumentDialog from './ConnectInstrumentDialog';
import GroupInstrumentsDialog from './GroupInstrumentsDialog';

import { Button, Grid, SelectChangeEvent } from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FlashOffIcon from '@mui/icons-material/FlashOff';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import GreenButton from '../../components/GreenButton';
import InfoPopup from '../../components/InfoPopup';
import PowerSettingsNew from '@mui/icons-material/PowerSettingsNew';
import { RestartAlt } from '@mui/icons-material';

import { useAnalyzer } from 'features/analyzers/api/getAnalyzer';
import { useUpdateAnalyzer } from 'features/analyzers/api/updateAnalyzer';
import { useDeleteAnalyzer } from 'features/analyzers/api/deleteAnalyzer';
import { useSwitches } from 'features/switches/api/getSwitches';
import { useUpdateSwitch } from 'features/switches/api/updateSwitch';
import { useDeleteSwitch } from 'features/switches/api/deleteSwitch';

import { Analyzer, Switch } from 'types';
import { queryClient } from 'lib/react-query';
import { InstrumentType } from '../analyzerDataSettings/types';
import { sendScpiCommand } from 'features/spectrasWebSocketServer/spectrasWebSocketServerSlice';
import { useAppDispatch } from 'app/hooks';
import ShutdownRebootDialog from './ShutdownRebootDialog';
import { useIsAdmin } from 'features/auth/hooks/useIsAdmin';

interface Props {
  selectedInstrumentType: InstrumentType;
  setSelectedInstrumentType: React.Dispatch<React.SetStateAction<number>>;
  selectedInstrumentId: number;
  setSelectedInstrumentId: React.Dispatch<React.SetStateAction<number>>;
}

const InstrumentConfiguration = ({
  selectedInstrumentType,
  setSelectedInstrumentType,
  selectedInstrumentId,
  setSelectedInstrumentId,
}: Props) => {
  const isAdmin = useIsAdmin();
  const dispatch = useAppDispatch();
  const { data: selectedAnalyzer } = useAnalyzer({
    analyzerId: selectedInstrumentId,
  });

  const { data: switches } = useSwitches({});

  const updateAnalyzerMutation = useUpdateAnalyzer({
    config: {
      onSuccess: (data: Analyzer) => {
        queryClient.invalidateQueries(['analyzer', data.id]);
        queryClient.invalidateQueries(['analyzers']);
      },
    },
  });
  const deleteAnalyzerMutation = useDeleteAnalyzer({});
  const updateSwitchMutation = useUpdateSwitch({});
  const deleteSwitchMutation = useDeleteSwitch({});

  const [snackbar, setSnackBar] = useState<{
    severity: 'success' | 'error';
    open: boolean;
    message: string;
  }>({
    severity: 'success',
    open: false,
    message: '',
  });

  const handleClose = () => {
    setSnackBar({ ...snackbar, open: false });
  };

  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shutdownRebootDialogOpen, setShutdownRebootDialogOpen] =
    useState(false);
  const [groupInstrumentsDialogOpen, setGroupInstrumentsDialogOpen] =
    useState(false);
  const [shutdownInstrument, setShutdownInstrument] = useState(false);

  const getSelectedInstrument = () => {
    if (selectedInstrumentType === InstrumentType.ANALYZER) {
      return selectedAnalyzer;
    } else {
      return switches?.find(
        (switchItem: Switch) => switchItem.id === selectedInstrumentId
      );
    }
  };

  const selectedInstrument = getSelectedInstrument();

  const handleInstrumentSelected = (instrumentId: number) => {
    setSelectedInstrumentId(instrumentId);
  };

  const handleConnectClicked = async () => {
    if (selectedInstrument?.enabled) return;
    const updatedInstrument = { id: selectedInstrumentId, enabled: true };

    if (selectedInstrumentType === InstrumentType.ANALYZER) {
      await updateAnalyzerMutation.mutateAsync({
        data: updatedInstrument,
        analyzerId: selectedInstrumentId,
      });
    } else {
      try {
        await updateSwitchMutation.mutateAsync({
          data: updatedInstrument,
          switchId: selectedInstrumentId,
        });
      } catch (e) {
        setSnackBar({
          open: true,
          severity: 'error',
          message: 'Switch limit exceeded.',
        });
      }
    }
  };

  const handleNewClicked = () => {
    setSelectedInstrumentId(-1);
    setConnectDialogOpen(true);
  };

  const handleReleaseClicked = async () => {
    const updatedInstrument = { id: selectedInstrumentId, enabled: false };

    if (selectedInstrumentType === InstrumentType.ANALYZER) {
      await updateAnalyzerMutation.mutateAsync({
        data: updatedInstrument,
        analyzerId: selectedInstrumentId,
      });
    } else {
      await updateSwitchMutation.mutateAsync({
        data: updatedInstrument,
        switchId: selectedInstrumentId,
      });
    }
  };

  const handleDeleteClicked = async () => {
    if (selectedInstrumentType === InstrumentType.ANALYZER) {
      await deleteAnalyzerMutation.mutateAsync(selectedInstrumentId);
    } else {
      await deleteSwitchMutation.mutateAsync(selectedInstrumentId);
    }
  };

  const handleShutdownOrReboot = () => {
    if (!shutdownInstrument) {
      dispatch(
        sendScpiCommand({
          analyzer: selectedInstrument as Analyzer,
          scpiCommand: ':SYST:REB',
        })
      );
    } else if (shutdownInstrument) {
      dispatch(
        sendScpiCommand({
          analyzer: selectedInstrument as Analyzer,
          scpiCommand: ':SYST:SHUT',
        })
      );
    }
  };

  const handleInstrumentTabChanged = (
    e: React.SyntheticEvent,
    newValue: number
  ) => {
    setSelectedInstrumentType(newValue);
    setSelectedInstrumentId(-1);
  };

  const handleInstrumentTypeChanged = (e: SelectChangeEvent) => {
    setSelectedInstrumentType(parseInt(e.target.value));
  };

  const handleConnectDialogClosed = () => {
    setConnectDialogOpen(false);
  };

  return (
    <>
      {selectedAnalyzer && (
        <GroupInstrumentsDialog
          open={groupInstrumentsDialogOpen}
          handleClose={() => setGroupInstrumentsDialogOpen(false)}
          selectedAnalyzerId={selectedAnalyzer.id}
        />
      )}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onSubmit={handleDeleteClicked}
        onClose={() => setDeleteDialogOpen(false)}
        itemToDelete={`Instrument ${selectedInstrument?.name}`}
      />
      <ShutdownRebootDialog
        open={shutdownRebootDialogOpen}
        shutdown={shutdownInstrument}
        onSubmit={handleShutdownOrReboot}
        onClose={() => setShutdownRebootDialogOpen(false)}
        instrumentName={selectedInstrument?.name || ''}
      />
      <ConnectInstrumentDialog
        open={connectDialogOpen}
        handleInstrumentTypeChanged={handleInstrumentTypeChanged}
        selectedInstrumentType={selectedInstrumentType}
        handleClose={handleConnectDialogClosed}
        selectedInstrument={selectedInstrument}
        setSnackBar={setSnackBar}
      />
      <Grid container direction='column' spacing={2}>
        <Grid container item spacing={1}>
          <Grid item>
            <Button
              onClick={() => setDeleteDialogOpen(true)}
              variant='contained'
              color='error'
              fullWidth
              disabled={!selectedInstrument}
            >
              <DeleteIcon />
              Delete
            </Button>
          </Grid>
          <Grid item>
            <GreenButton
              onClick={handleNewClicked}
              name='New'
              icon={<AddIcon />}
              fullWidth
            />
          </Grid>
          <Grid item>
            <Button
              onClick={handleReleaseClicked}
              variant='contained'
              color='warning'
              fullWidth
              disabled={!selectedInstrument}
            >
              <FlashOffIcon />
              Release
            </Button>
          </Grid>
          <Grid item>
            <Button
              onClick={handleConnectClicked}
              variant='contained'
              color='primary'
              fullWidth
              //enabledAnalyzer?.legnth > 2 || is for active unit disability check
              disabled={/*enabledAnalyzers?.length > 2 ||*/ !selectedInstrument}
            >
              <FlashOnIcon />
              Connect
            </Button>
          </Grid>
          {selectedInstrument?.type === 0 && isAdmin && (
            <>
              <Grid item>
                <Button
                  onClick={() => {
                    setShutdownInstrument(false);
                    setShutdownRebootDialogOpen(true);
                  }}
                  variant='contained'
                  color='warning'
                  fullWidth
                  disabled={!selectedInstrument}
                >
                  <RestartAlt />
                  Reboot
                </Button>
              </Grid>
              <Grid item>
                <Button
                  onClick={() => {
                    setShutdownInstrument(true);
                    setShutdownRebootDialogOpen(true);
                  }}
                  variant='contained'
                  color='error'
                  fullWidth
                  disabled={!selectedInstrument}
                >
                  <PowerSettingsNew />
                  Shutdown
                </Button>
              </Grid>
            </>
          )}
          {selectedInstrumentType === InstrumentType.ANALYZER &&
            selectedAnalyzer && (
              <Grid item>
                <Button
                  onClick={() => setGroupInstrumentsDialogOpen(true)}
                  variant='contained'
                  color='primary'
                  fullWidth
                  disabled={!selectedAnalyzer}
                >
                  Group
                </Button>
              </Grid>
            )}
        </Grid>
        <InstrumentTabPanel
          handleInstrumentSelected={handleInstrumentSelected}
          selectedInstrumentType={selectedInstrumentType}
          setSelectedInstrumentType={handleInstrumentTabChanged}
          setConnectDialogOpen={() => setConnectDialogOpen(true)}
          switches={switches}
          selectedInstrumentId={selectedInstrumentId}
        />
      </Grid>
      <InfoPopup
        openCondition={snackbar.open}
        title={null}
        message={snackbar.message}
        error={snackbar.severity === 'error'}
        success={snackbar.severity === 'success'}
        handleClose={handleClose}
      />
    </>
  );
};

export default InstrumentConfiguration;
